import { memo } from 'react'
import { Error, Loader } from '@myap/ui-library/cjs/components'
import { SortableTable } from '../../common'
import ApprovalRequestRow from './ApprovalRequestRow'
import {
  selectFilteredApprovalRequests,
  selectSelectedApprovalRequestId,
  selectNumApprovalRequests,
  selectApprovalRequestStatusProps,
} from '../../../selectors/approvalRequests'
import { selectCurrentFilter } from '../../../selectors/filters'
import { isEmpty, generateShortUID } from '../../../utils/common'
import { groupBy, sortColumnByKey } from '../../../utils/sort'
import tableUtilStyles from '../../../assets/style/scss/table-utils.scss'

const mapStateToProps = (state, ownProps) => {
  const { headerColumns, idField, summary, caption, approvalRequestColumns = [] } = ownProps
  const { fetching, error } = selectApprovalRequestStatusProps(state)
  const visibleApprovalRequests = selectFilteredApprovalRequests(state) || []
  const numApprovalRequests = selectNumApprovalRequests(state)
  const { groupByAICode } = selectCurrentFilter(state)
  const {
    app: { smallViewport },
  } = state
  const selectedApprovalRequestId = selectSelectedApprovalRequestId(state)

  return {
    fetching,
    error,
    headerColumns,
    idField,
    summary,
    caption,
    groupByAICode,
    approvalRequestColumns,
    visibleApprovalRequests,
    selectedApprovalRequest: !isEmpty(selectedApprovalRequestId),
    selectedApprovalRequestId,
    allApprovalRequestsHidden: visibleApprovalRequests.length === 0 && numApprovalRequests > 0,
    smallViewport,
  }
}

const ITEM_SIZE = 73
const DISPLAY_ROWS = 20

const ApprovalRequestsTable = ({
  visibleApprovalRequests = [],
  selectedApprovalRequest,
  selectedApprovalRequestId,
  allApprovalRequestsHidden,
  headerColumns,
  fetching,
  error,
  idField,
  summary,
  caption,
  groupByAICode,
  approvalRequestColumns,
  smallViewport,
}) => {
  // Sort by aiCode before grouping, note that sort is by ASCII order where 0 comes after 9
  const groupedARs = groupByAICode
    ? groupBy(sortColumnByKey(visibleApprovalRequests, 'aiCode', ['asc']), 'aiCode')
    : []
  const tableWrapperStyle = selectedApprovalRequest ? { paddingRight: 0 } : {}

  if (allApprovalRequestsHidden)
    return (
      <div className="col-xs-12">
        <p style={{ marginTop: '20px' }}>
          There are no approval requests that match the selected filters.
        </p>
      </div>
    )

  if (error) {
    return <Error message={error} />
  }

  if (fetching) {
    return <Loader />
  }

  if (!selectedApprovalRequest || (selectedApprovalRequest && !smallViewport)) {
    return (
      <div
        className={`col-lg-${selectedApprovalRequest ? '6' : '12'} col-md-${
          selectedApprovalRequest ? '7' : '12'
        }`}
        style={tableWrapperStyle}
      >
        <SortableTable
          tableAttributes={{
            id: 'approvalRequestsTable',
            summary,
            className: `table ${
              !groupByAICode ? `${tableUtilStyles['som-striped']} ` : ''
            }cb-no-table-border ${tableUtilStyles['clickable-table']}`,
          }}
          tableCaption={{
            caption,
            srOnly: true,
          }}
          data={!groupByAICode ? visibleApprovalRequests : groupedARs}
          virtualRow={memo(({ data, index, totalCount }) => (
            <ApprovalRequestRow
              key={data[index][idField]}
              approvalRequest={data[index]}
              idField={idField}
              approvalRequestColumns={approvalRequestColumns}
              grouped={groupByAICode}
              index={index}
              totalCount={totalCount}
              itemSize={ITEM_SIZE}
            />
          ))}
          StandardRow={memo(({ data, index, totalCount }) => (
            <ApprovalRequestRow
              key={data[idField]}
              approvalRequest={data}
              idField={idField}
              approvalRequestColumns={approvalRequestColumns}
              grouped={groupByAICode}
              index={index}
              totalCount={totalCount}
            />
          ))}
          virtualKey={() => generateShortUID()}
          standardKey={() => generateShortUID()}
          columns={headerColumns}
          hiddenColumns={groupByAICode ? ['aiCode'] : []}
          itemSize={ITEM_SIZE}
          height={ITEM_SIZE * DISPLAY_ROWS}
          style={{ fontSize: '0.6rem' }}
          isGrouped={groupByAICode}
          flattenFunction={groupedData => {
            const processedGroupedARs = []
            const aiCodes = Object.keys(groupedData)
            aiCodes.forEach(aiCode => {
              processedGroupedARs.push({
                orgRow: true,
                aiCode,
                contiguousUSInd: groupedARs[aiCode][0].contiguousUSInd,
              })
              groupedARs[aiCode].forEach(row => {
                processedGroupedARs.push({ orgRow: false, ...row })
              })
            })
            return processedGroupedARs
          }}
          selectedItem={selectedApprovalRequestId}
          searchIndexFunc={element => element[idField] === selectedApprovalRequestId}
        />
      </div>
    )
  }
  return null
}

export default connect(mapStateToProps)(ApprovalRequestsTable)
